/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  Tooltip,
  VStack,
  useBoolean,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import Select from 'react-select';
import AppColors from '../../../constants/AppColors';
import { FONT_FAMILY } from '../../../constants/Theme';
import { EyeAvatarIcon, WarningText } from '../../../constants/IconData';
import { AuditContext } from '../../../providers/AuditProvider';
import AuditSheetFileViewer from '../../audit_sheet/components/AuditSheetFileViewer';
import { AttachmentCell } from './tables/table_config';

export const ASSIGN_FIELDS = {
  HOSPITAL: 'hospital',
  SPECIALTY: 'specialty',
  AUDITOR: 'auditor',
  QA: 'qa',
};

const unique = list => [...new Set(list.filter(Boolean))];

// The value the selected charts share for a field, or null when they differ
// (or none is set). `names` is what they currently have, for the note below.
const sharedOption = (items, getId, getLabel) => {
  const byId = new Map();
  items.forEach(item => {
    const id = getId(item);
    if (id != null) {
      byId.set(id, getLabel(item));
    }
  });
  const names = [...byId.values()];
  return {
    current: byId.size === 1 ? { value: [...byId.keys()][0], label: names[0] } : null,
    names,
  };
};

const isDirty = (pending, current) => !!pending && pending.value !== current?.value;

const FieldSelect = React.forwardRef(
  (
    {
      label,
      hint,
      options,
      value,
      onChange,
      placeholder,
      isLoading = false,
      disabledReason = '',
      currentNames = [],
      isDirty = false,
      pluralLabel = 'values',
    },
    ref
  ) => (
    <Box minW={0}>
      <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
        {label}
        {hint && (
          <Text as="span" fontWeight="normal" color="gray.500">
            {' '}
            {hint}
          </Text>
        )}
      </Text>
      <Tooltip
        hasArrow
        placement="top"
        label={disabledReason}
        isDisabled={!disabledReason}
      >
        <div>
          <Select
            ref={ref}
            options={options}
            onChange={onChange}
            value={value}
            isClearable={isDirty}
            placeholder={
              currentNames.length > 1
                ? `${currentNames.length} ${pluralLabel} selected`
                : placeholder
            }
            isDisabled={!!disabledReason}
            isLoading={isLoading}
            menuPlacement="auto"
            maxMenuHeight={200}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 1500 }),
              control: base => ({
                ...base,
                borderColor: isDirty ? '#3182CE' : base.borderColor,
                boxShadow: isDirty ? '0 0 0 1px #3182CE' : base.boxShadow,
              }),
            }}
          />
        </div>
      </Tooltip>
      <Text
        fontSize="xs"
        color={isDirty ? 'blue.500' : 'gray.500'}
        mt={1}
        noOfLines={1}
        title={currentNames.length > 1 ? currentNames.join(', ') : undefined}
      >
        {isDirty
          ? `Will change to ${value.label} on save`
          : currentNames.length
          ? `Current: ${currentNames.join(', ')}`
          : 'Not assigned yet'}
      </Text>
    </Box>
  )
);

/**
 * Assignment workflow for one batch: pick the charts, then hospital (batch
 * wide) -> specialty -> auditor -> QA. Every dropdown starts on what the
 * selected charts already have; Save sends only what changed and keeps the
 * modal open so the manager can carry on with the next step. The chart list
 * reads straight from the provider so it reflects every save.
 */
const AssignChartsModal = ({ isOpen, onClose, batchUploadId, focusField }) => {
  const {
    allCharts,
    updateChartsV2,
    getClientHospitals,
    getClientSpecialties,
    getCommonReviewers,
    isFetchingClientHospitals,
    isFetchingSpecialties,
  } = useContext(AuditContext);

  const [search, setSearch] = useState('');
  const [selectedChartIds, setSelectedChartIds] = useState([]);

  const [hospitals, setHospitals] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [qas, setQas] = useState([]);
  const [isFetchingReviewers, ifrState] = useBoolean(false);

  // what the user picked; null means "leave as it is"
  const [pendingHospital, setPendingHospital] = useState(null);
  const [pendingSpecialty, setPendingSpecialty] = useState(null);
  const [pendingAuditor, setPendingAuditor] = useState(null);
  const [pendingQa, setPendingQa] = useState(null);

  const [isSaving, isState] = useBoolean(false);
  const [savedNote, setSavedNote] = useState('');

  const hospitalRef = useRef(null);
  const specialtyRef = useRef(null);
  const auditorRef = useRef(null);
  const qaRef = useRef(null);
  const focusRefs = {
    [ASSIGN_FIELDS.HOSPITAL]: hospitalRef,
    [ASSIGN_FIELDS.SPECIALTY]: specialtyRef,
    [ASSIGN_FIELDS.AUDITOR]: auditorRef,
    [ASSIGN_FIELDS.QA]: qaRef,
  };

  const batch = useMemo(
    () => (allCharts ?? []).find(b => b.upload_id === batchUploadId),
    [allCharts, batchUploadId]
  );

  const charts = useMemo(
    () =>
      (batch?.subRows ?? []).map(chart => ({
        ...chart,
        all_attachments: [
          chart.file_obj?.name,
          chart.file_obj?.audited_chart_name,
          ...(chart.attachments?.map(a => a.file.name) ?? []),
        ].filter(Boolean),
      })),
    [batch]
  );

  // Every open starts clean - nothing picked for a previous batch (or left
  // unsaved last time) may leak into this one.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setSearch('');
    setSavedNote('');
    setPendingHospital(null);
    setPendingSpecialty(null);
    setPendingAuditor(null);
    setPendingQa(null);
    setSelectedChartIds((batch?.subRows ?? []).map(chart => String(chart.id)));

    setHospitals([]);
    if (batch?.uploader?.id) {
      getClientHospitals({ client_id: batch.uploader.id }).then(res =>
        setHospitals(Array.isArray(res) ? res : [])
      );
    }
  }, [isOpen, batchUploadId]);

  // the specialties on offer depend on the batch's hospital
  useEffect(() => {
    if (!isOpen || !batch) {
      return;
    }
    let cancelled = false;
    const query = batch.hospital?.id
      ? { hospital_id: batch.hospital.id }
      : { client_id: batch.uploader?.id };
    getClientSpecialties(query).then(res => {
      if (!cancelled) {
        setSpecialties(Array.isArray(res) ? res : []);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, batchUploadId, batch?.hospital?.id]);

  const selectedCharts = useMemo(() => {
    const ids = new Set(selectedChartIds);
    return charts.filter(chart => ids.has(String(chart.id)));
  }, [charts, selectedChartIds]);

  // ── what the selected charts have right now ──────────────────────────────
  const currentHospital = useMemo(() => {
    if (!batch?.hospital) {
      return { current: null, names: [] };
    }
    const label = `${batch.hospital.name} - ${batch.hospital.state ?? batch.hospital.state_abbr ?? ''}`;
    return { current: { value: batch.hospital.id, label }, names: [label] };
  }, [batch?.hospital]);
  const currentSpecialty = useMemo(
    () => sharedOption(selectedCharts, c => c.specialty?.id, c => c.specialty?.name),
    [selectedCharts]
  );
  const currentAuditor = useMemo(
    () => sharedOption(selectedCharts, c => c.auditor?.id, c => c.auditor?.full_name),
    [selectedCharts]
  );
  const currentQa = useMemo(
    () => sharedOption(selectedCharts, c => c.qa?.id, c => c.qa?.full_name),
    [selectedCharts]
  );

  const hospitalDirty = isDirty(pendingHospital, currentHospital.current);
  const specialtyDirty = isDirty(pendingSpecialty, currentSpecialty.current);
  const auditorDirty = isDirty(pendingAuditor, currentAuditor.current);
  const qaDirty = isDirty(pendingQa, currentQa.current);

  const hasChartWithoutSpecialty = selectedCharts.some(c => !c.specialty);

  // Reviewers must be mapped to every specialty the selected charts will have
  // once saved - the one being picked now, or else the ones already on them.
  const reviewerSpecKey = useMemo(() => {
    if (pendingSpecialty) {
      return String(pendingSpecialty.value);
    }
    return unique(selectedCharts.map(c => c.specialty?.id))
      .sort((a, b) => a - b)
      .join(',');
  }, [pendingSpecialty, selectedCharts]);

  useEffect(() => {
    setPendingAuditor(null);
    setPendingQa(null);
    setAuditors([]);
    setQas([]);
    ifrState.off();
    if (!isOpen || !reviewerSpecKey) {
      return;
    }
    let cancelled = false;
    ifrState.on();
    Promise.all([
      getCommonReviewers({ specialty_ids: reviewerSpecKey, auditor: true }),
      getCommonReviewers({ specialty_ids: reviewerSpecKey, qa: true }),
    ]).then(([auditorRes, qaRes]) => {
      if (cancelled) {
        return;
      }
      setAuditors(Array.isArray(auditorRes) ? auditorRes : []);
      setQas(Array.isArray(qaRes) ? qaRes : []);
      ifrState.off();
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, reviewerSpecKey]);

  // Changing the specialty unassigns anyone on the chart who isn't mapped to
  // it (the server does the same) - work out who, so we can warn before saving.
  const chartsLosingReviewer = useMemo(() => {
    const losing = { auditor: [], qa: [] };
    if (!specialtyDirty || isFetchingReviewers) {
      return losing;
    }
    const auditorIds = new Set(auditors.map(u => u.id));
    const qaIds = new Set(qas.map(u => u.id));
    selectedCharts.forEach(chart => {
      if (!pendingAuditor && chart.auditor && !auditorIds.has(chart.auditor.id)) {
        losing.auditor.push(chart);
      }
      if (!pendingQa && chart.qa && !qaIds.has(chart.qa.id)) {
        losing.qa.push(chart);
      }
    });
    return losing;
  }, [specialtyDirty, pendingAuditor, pendingQa, auditors, qas, selectedCharts, isFetchingReviewers]);

  const unassignWarning = useMemo(() => {
    const parts = [];
    [
      ['auditor', 'Auditor'],
      ['qa', 'QA'],
    ].forEach(([key, label]) => {
      const lost = chartsLosingReviewer[key];
      if (lost.length) {
        const names = unique(lost.map(c => c[key]?.full_name));
        parts.push(
          `${label} ${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} not mapped to ${pendingSpecialty?.label} and will be unassigned from ${lost.length} chart${lost.length === 1 ? '' : 's'}`
        );
      }
    });
    return parts.length
      ? `${parts.join('. ')}. Pick a replacement to reassign in the same save.`
      : '';
  }, [chartsLosingReviewer]);

  const reviewerDisabledReason = useMemo(() => {
    if (!selectedCharts.length) {
      return 'Select 1 or more charts';
    }
    if (!pendingSpecialty && hasChartWithoutSpecialty) {
      return 'Pick a specialty first - some selected charts have none';
    }
    return '';
  }, [selectedCharts, pendingSpecialty, hasChartWithoutSpecialty]);

  const qaDisabledReason = useMemo(() => {
    if (reviewerDisabledReason) {
      return reviewerDisabledReason;
    }
    if (pendingAuditor) {
      return '';
    }
    const losingAuditor = new Set(chartsLosingReviewer.auditor.map(c => c.id));
    const allHaveAuditor = selectedCharts.every(
      c => c.auditor && !losingAuditor.has(c.id)
    );
    return allHaveAuditor
      ? ''
      : 'Pick an auditor first - some selected charts have none';
  }, [reviewerDisabledReason, pendingAuditor, chartsLosingReviewer, selectedCharts]);

  useEffect(() => {
    if (qaDisabledReason) {
      setPendingQa(null);
    }
  }, [qaDisabledReason]);

  // What each dropdown shows: the user's pick, else what the charts already
  // have. A reviewer about to be unassigned is shown as empty, not as current.
  const shownHospital = pendingHospital ?? currentHospital.current;
  const shownSpecialty = pendingSpecialty ?? currentSpecialty.current;
  const shownAuditor =
    pendingAuditor ?? (chartsLosingReviewer.auditor.length ? null : currentAuditor.current);
  const shownQa = pendingQa ?? (chartsLosingReviewer.qa.length ? null : currentQa.current);

  const filteredCharts = useMemo(() => {
    const query = search.toLowerCase();
    if (!query) {
      return charts;
    }
    return charts.filter(chart =>
      [
        chart.name,
        chart.specialty?.name,
        chart.auditor?.full_name,
        chart.qa?.full_name,
        ...chart.all_attachments,
      ].some(val => (val ?? '').toLowerCase().includes(query))
    );
  }, [search, charts]);

  const filteredIds = filteredCharts.map(chart => String(chart.id));
  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every(id => selectedChartIds.includes(id));

  const toggleSelectAllFiltered = checked => {
    if (checked) {
      setSelectedChartIds(unique([...selectedChartIds, ...filteredIds]));
    } else {
      setSelectedChartIds(selectedChartIds.filter(id => !filteredIds.includes(id)));
    }
  };

  const hasChartChanges = specialtyDirty || auditorDirty || qaDirty;
  const hasChanges = hospitalDirty || hasChartChanges;
  const canSave = hasChanges && (!hasChartChanges || selectedCharts.length > 0);

  const handleSave = async () => {
    isState.on();
    setSavedNote('');
    try {
      const saved = [];
      // the hospital goes batch-wide on purpose: a batch belongs to one hospital
      if (hospitalDirty) {
        const response = await updateChartsV2(
          { batch_id: batchUploadId, hospital_id: pendingHospital.value },
          batchUploadId
        );
        if (response.error) {
          return;
        }
        setPendingHospital(null);
        saved.push('hospital');
      }
      if (hasChartChanges) {
        const response = await updateChartsV2(
          {
            chart_ids: selectedCharts.map(chart => chart.id),
            ...(specialtyDirty && { specialty_id: pendingSpecialty.value }),
            ...(auditorDirty && { auditor_id: pendingAuditor.value }),
            ...(qaDirty && { qa_id: pendingQa.value }),
          },
          batchUploadId
        );
        if (response.error) {
          return;
        }
        setPendingSpecialty(null);
        setPendingAuditor(null);
        setPendingQa(null);
        if (specialtyDirty) saved.push('specialty');
        if (auditorDirty) saved.push('auditor');
        if (qaDirty) saved.push('QA');
      }
      setSavedNote(`Saved ${saved.join(', ')}. Keep going, or close when you're done.`);
    } finally {
      isState.off();
    }
  };

  const chartListHeader = ['File', 'Specialty', 'Auditor', 'QA'];
  const chartColumns = '26% 22% 22% 22% 8%';

  return (
    <Modal
      isOpen={isOpen}
      size="6xl"
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
      initialFocusRef={focusRefs[focusField] ?? specialtyRef}
    >
      <ModalOverlay style={{ backgroundColor: AppColors.lightSkyBlue }} />

      <ModalContent maxW="min(1180px, 95vw)" maxH="92vh" borderRadius="lg">
        <ModalHeader pb={2}>
          <Text
            fontSize={'xl'}
            color={AppColors.secondary}
            fontFamily={FONT_FAMILY.baiJamurjee}
            fontWeight="bold"
            noOfLines={1}
          >
            {batch?.name || ''}
          </Text>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody pt={2} pb={4}>
          <Flex direction={{ base: 'column', lg: 'row' }} gap={5} align="stretch">
            {/* ── Left: charts the assignment applies to ── */}
            <Box flex="1 1 0" minW={0} display="flex" flexDirection="column">
              <Box
                border="1px solid #E2E8F0"
                borderRadius="md"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                flex="1"
                minH={0}
              >
                <HStack px={3} py={2} borderBottom="1px solid #E2E8F0" justify="space-between">
                  <Checkbox
                    isChecked={allFilteredSelected}
                    onChange={e => toggleSelectAllFiltered(e.target.checked)}
                    whiteSpace="nowrap"
                  >
                    <Text fontSize="sm">
                      Select all
                      <Text as="span" color="gray.500" ml={2}>
                        {selectedCharts.length} of {charts.length} selected
                      </Text>
                    </Text>
                  </Checkbox>
                  <InputGroup size="sm" w="220px">
                    <Input
                      placeholder="Search charts"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      borderRadius="md"
                    />
                    <InputRightElement pointerEvents="none">
                      <SearchIcon color="gray.400" boxSize={3} />
                    </InputRightElement>
                  </InputGroup>
                </HStack>

                <Grid
                  templateColumns={chartColumns}
                  px={3}
                  py={1}
                  bg="gray.50"
                  borderBottom="1px solid #E2E8F0"
                >
                  {chartListHeader.map(title => (
                    <Text key={title} fontSize="xs" fontWeight="semibold" color="gray.600" px={title === 'File' ? 0 : 2}>
                      {title}
                    </Text>
                  ))}
                </Grid>

                <Box
                  overflowY="auto"
                  p={2}
                  maxH={{ base: '220px', lg: 'calc(92vh - 300px)' }}
                  minH={{ lg: '260px' }}
                >
                  <CheckboxGroup value={selectedChartIds} onChange={setSelectedChartIds}>
                    <VStack align="stretch" spacing={0} width={'100%'}>
                      {filteredCharts.length > 0 ? (
                        filteredCharts.map(chart => (
                          <Box
                            as="label"
                            key={chart.id}
                            px={1}
                            py={1.5}
                            _hover={{ bg: 'gray.50' }}
                            cursor="pointer"
                            width="100%"
                            borderBottom={'1px solid'}
                            borderColor="gray.100"
                          >
                            <Grid templateColumns={chartColumns} w="100%" alignItems="center">
                              <HStack spacing={2} minW={0}>
                                <Checkbox value={String(chart.id)} />
                                <Tooltip label={chart.name} hasArrow openDelay={300}>
                                  <Text fontSize="sm" noOfLines={1}>
                                    {chart.name}
                                  </Text>
                                </Tooltip>
                              </HStack>

                              {[chart.specialty?.name, chart.auditor?.full_name, chart.qa?.full_name].map(
                                (val, idx) => (
                                  <Box key={idx} px={2} minW={0} borderLeft="1px solid" borderColor="gray.200">
                                    <AttachmentCell attachments={[val].filter(Boolean)} />
                                  </Box>
                                )
                              )}

                              <Box display="flex" alignItems="center" justifyContent="flex-end">
                                <AuditSheetFileViewer
                                  auditData={chart}
                                  icon={
                                    <EyeAvatarIcon
                                      style={{ cursor: 'pointer', width: 26, height: 26 }}
                                      onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                                      onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                                    />
                                  }
                                  chart_id={chart.id}
                                />
                              </Box>
                            </Grid>
                          </Box>
                        ))
                      ) : (
                        <Text fontSize="sm" color="gray.400" p={2}>
                          No results found
                        </Text>
                      )}
                    </VStack>
                  </CheckboxGroup>
                </Box>

                {!selectedCharts.length && (
                  <Box px={3} py={1.5} bg="gray.50" borderTop="1px solid #E2E8F0">
                    <WarningText message="Select 1 or more charts to assign" />
                  </Box>
                )}
              </Box>
            </Box>

            {/* ── Right: Hospital -> Specialty -> Auditor -> QA ── */}
            <Box
              flex={{ base: '1 1 auto', lg: '0 0 340px' }}
              bg="gray.50"
              border="1px solid #E2E8F0"
              borderRadius="md"
              p={4}
            >
              <VStack align="stretch" spacing={4}>
                <FieldSelect
                  ref={hospitalRef}
                  label="Hospital"
                  hint="(whole batch)"
                  options={hospitals.map(val => ({
                    value: val.id,
                    label: `${val.name} - ${val.state}`,
                  }))}
                  value={shownHospital}
                  onChange={setPendingHospital}
                  placeholder="Select Hospital"
                  isLoading={isFetchingClientHospitals}
                  currentNames={currentHospital.names}
                  isDirty={hospitalDirty}
                />
                <FieldSelect
                  ref={specialtyRef}
                  label="Specialty"
                  pluralLabel="specialties"
                  options={specialties.map(val => ({ value: val.id, label: val.name }))}
                  value={shownSpecialty}
                  onChange={setPendingSpecialty}
                  placeholder="Select Specialty"
                  isLoading={isFetchingSpecialties}
                  disabledReason={selectedCharts.length ? '' : 'Select 1 or more charts'}
                  currentNames={currentSpecialty.names}
                  isDirty={specialtyDirty}
                />
                <FieldSelect
                  ref={auditorRef}
                  label="Auditor"
                  pluralLabel="auditors"
                  options={auditors.map(val => ({ value: val.id, label: val.full_name }))}
                  value={shownAuditor}
                  onChange={setPendingAuditor}
                  placeholder="Select Auditor"
                  isLoading={isFetchingReviewers}
                  disabledReason={reviewerDisabledReason}
                  currentNames={currentAuditor.names}
                  isDirty={auditorDirty}
                />
                <FieldSelect
                  ref={qaRef}
                  label="QA"
                  pluralLabel="QAs"
                  options={qas.map(val => ({ value: val.id, label: val.full_name }))}
                  value={shownQa}
                  onChange={setPendingQa}
                  placeholder="Select QA"
                  isLoading={isFetchingReviewers}
                  disabledReason={qaDisabledReason}
                  currentNames={currentQa.names}
                  isDirty={qaDirty}
                />
                <WarningText message={unassignWarning} />
              </VStack>
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter borderTop="1px solid #E2E8F0" py={3}>
          <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isSaving}>
            Close
          </Button>
          <Spacer />
          <Text fontSize="sm" mr={4} color={hasChanges ? 'blue.500' : 'green.500'} textAlign="right">
            {hasChanges
              ? hasChartChanges
                ? `Applies to ${selectedCharts.length} selected chart${selectedCharts.length === 1 ? '' : 's'}`
                : 'Applies to the whole batch'
              : savedNote}
          </Text>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={!canSave || isFetchingReviewers}
          >
            Save changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AssignChartsModal;
